import {
  IPageVisit,
  IPracticeSetupStep,
} from '@principle-theorem/principle-core/interfaces';
import {
  IPageVisitTrackingConfig,
  IValidationResult,
} from './practice-setup-step.types';

export class PracticeSetupStepValidation {
  /**
   * Validates whether a practice setup step's page visits satisfy the given tracking configuration.
   *
   * @param step - The practice setup step containing page visit records to validate.
   * @param config - The tracking configuration specifying routes and validation mode:
   *   - `'any'`: valid if at least one matching route was visited.
   *   - `'count'`: valid if the number of unique matching visits meets `requiredCount`.
   *   - `'all'`: valid if the number of unique matching visits meets the provided `entityCount`.
   * @param entityCount - The total number of entities expected to be visited (required for `'all'` mode).
   * @returns A validation result indicating success or failure, with optional progress tracking.
   */
  static validatePageVisits(
    step: IPracticeSetupStep | undefined,
    config: IPageVisitTrackingConfig,
    entityCount?: number
  ): IValidationResult {
    const matchCount = PracticeSetupStepValidation._countUniqueMatchingVisits(
      step,
      config
    );

    switch (config.validationMode) {
      case 'any':
        return PracticeSetupStepValidation._validateAnyMode(matchCount);
      case 'count':
        return PracticeSetupStepValidation._validateCountMode(
          matchCount,
          config.requiredCount
        );
      case 'all':
        return PracticeSetupStepValidation._validateAllMode(
          matchCount,
          entityCount
        );
      default:
        return { valid: false, reason: 'Unknown validation mode' };
    }
  }

  static getRouteMatchSegments(route: string): string[] {
    return route
      .split('/')
      .filter(
        (segment) =>
          segment.length > 0 &&
          !(segment.startsWith('{') && segment.endsWith('}'))
      );
  }

  static hasMatchingRouteSegment(visit: IPageVisit, routes: string[]): boolean {
    return routes.some((route) => {
      const segments = PracticeSetupStepValidation.getRouteMatchSegments(route);

      if (segments.length === 0) {
        return false;
      }

      const fullRoute = segments.join('/');
      if (visit.route.includes(fullRoute)) {
        return true;
      }

      const lastSegment = segments[segments.length - 1];
      return visit.route.includes(lastSegment);
    });
  }

  static isCountableMatch(
    visit: IPageVisit,
    config: IPageVisitTrackingConfig
  ): boolean {
    return config.routes.some((route) => {
      const segments = PracticeSetupStepValidation.getRouteMatchSegments(route);

      if (segments.length === 0) {
        return false;
      }

      const fullRoute = segments.join('/');
      if (visit.route.includes(`${fullRoute}/`)) {
        return true;
      }

      const lastSegment = segments[segments.length - 1];
      return visit.route.includes(`${lastSegment}/`);
    });
  }

  private static _countUniqueMatchingVisits(
    step: IPracticeSetupStep | undefined,
    config: IPageVisitTrackingConfig
  ): number {
    const matchingVisits = PracticeSetupStepValidation._getMatchingVisits(
      step,
      config
    );
    const uniqueMatchingVisitRoutes = new Set(
      matchingVisits.map((visit) => visit.route)
    );
    return uniqueMatchingVisitRoutes.size;
  }

  private static _validateAnyMode(matchCount: number): IValidationResult {
    if (matchCount > 0) {
      return { valid: true };
    }
    return { valid: false, reason: 'Page not visited' };
  }

  private static _validateCountMode(
    matchCount: number,
    configuredRequiredCount: number | undefined
  ): IValidationResult {
    const requiredCount = configuredRequiredCount ?? 1;
    const progress = { current: matchCount, total: requiredCount };

    if (matchCount >= requiredCount) {
      return { valid: true, progress };
    }
    return {
      valid: false,
      reason: `Need ${requiredCount} visits, have ${matchCount}`,
      progress,
    };
  }

  private static _validateAllMode(
    matchCount: number,
    entityCount: number | undefined
  ): IValidationResult {
    if (entityCount === undefined) {
      return { valid: false, reason: 'Entity count not provided' };
    }
    if (entityCount === 0) {
      return { valid: true, progress: { current: 0, total: 0 } };
    }

    const progress = { current: matchCount, total: entityCount };
    if (matchCount >= entityCount) {
      return { valid: true, progress };
    }
    return {
      valid: false,
      reason: `Visited ${matchCount}/${entityCount}`,
      progress,
    };
  }

  private static _getMatchingVisits(
    step: IPracticeSetupStep | undefined,
    config: IPageVisitTrackingConfig
  ): IPageVisit[] {
    const pageVisits = step?.pageVisits ?? [];
    return pageVisits.filter((visit) =>
      PracticeSetupStepValidation._isVisitMatch(visit, config)
    );
  }

  private static _isVisitMatch(
    visit: IPageVisit,
    config: IPageVisitTrackingConfig
  ): boolean {
    if (PracticeSetupStepValidation._isCountingMode(config)) {
      return PracticeSetupStepValidation.isCountableMatch(visit, config);
    }
    return PracticeSetupStepValidation.hasMatchingRouteSegment(
      visit,
      config.routes
    );
  }

  private static _isCountingMode(config: IPageVisitTrackingConfig): boolean {
    return config.validationMode === 'count' || config.validationMode === 'all';
  }
}
